import BigNumber from 'bignumber.js'

/**
 * Pure (react/swr-free) part of the token price source: URL choice, response validation and
 * status derivation. Kept separate so it can be unit-tested inside this package, which has
 * react only as a peer dependency. The hooks live in ./useCakePrice.
 */
export type PriceToken = 'cadinu' | 'cbon' | 'cake' | 'bnb'
export type PriceStatus = 'loading' | 'ready' | 'stale' | 'unavailable'

export interface TokenPrice {
  price: BigNumber
  updatedAt?: number
}

export interface TokenPriceState {
  price?: BigNumber
  status: PriceStatus
  updatedAt?: number
  error?: string
  retry: () => void
}

export const PRICE_API_ORIGIN = 'https://farms.cadinu.io'
export const PRICE_STALE_AFTER_MS = 30 * 60_000
export const PRICE_REFRESH_MS = 10_000
const PRICE_FETCH_TIMEOUT_MS = 8_000

export const parsePriceResponse = (json: any, source: string): TokenPrice => {
  const raw = json?.price
  const asNumber = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN
  if (!Number.isFinite(asNumber) || asNumber <= 0) {
    throw new Error(`Invalid price from ${source}: ${JSON.stringify(raw)}`)
  }
  const updatedAtMs = typeof json?.updatedAt === 'string' ? Date.parse(json.updatedAt) : NaN
  return {
    price: new BigNumber(typeof raw === 'number' ? raw.toString() : raw),
    updatedAt: Number.isFinite(updatedAtMs) ? updatedAtMs : undefined,
  }
}

/**
 * Where a price is read. CADINU, CBON and BNB come only from the app's own /api/price (the pool CADINU trades in, BNB at the market;
 * site review 59 #3): the farms.cadinu.io service is never a fallback for them, because it answered the same CADINU price for days. CAKE
 * keeps that service as its fallback. Server-side there is no relative URL, so only CAKE has a source there.
 */
const OWN_ONLY = new Set<PriceToken>(['cadinu', 'cbon', 'bnb'])
export const priceUrlsFor = (token: PriceToken, sameOrigin = typeof window !== 'undefined'): string[] => {
  if (OWN_ONLY.has(token)) return sameOrigin ? [`/api/price/${token}`] : []
  return sameOrigin ? [`/api/price/${token}`, `${PRICE_API_ORIGIN}/price/${token}`] : [`${PRICE_API_ORIGIN}/price/${token}`]
}

const fetchWithTimeout = async (url: string, timeoutMs: number, fetcher: typeof fetch): Promise<Response> => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined
  const timer = setTimeout(() => controller?.abort(), timeoutMs)
  try {
    return await fetcher(url, { signal: controller?.signal, headers: { Accept: 'application/json' } })
  } finally {
    clearTimeout(timer)
  }
}

export const fetchTokenPrice = async (token: PriceToken, fetcher: typeof fetch = fetch): Promise<TokenPrice> => {
  let lastError: unknown
  for (const url of priceUrlsFor(token)) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await fetchWithTimeout(url, PRICE_FETCH_TIMEOUT_MS, fetcher)
      if (!res.ok) {
        throw new Error(`${url} answered ${res.status}`)
      }
      // eslint-disable-next-line no-await-in-loop
      return parsePriceResponse(await res.json(), url)
    } catch (error) {
      lastError = error
    }
  }
  throw lastError instanceof Error ? lastError : new Error(`Price for ${token} unavailable`)
}

export const derivePriceStatus = (data: TokenPrice | undefined, error: unknown, now = Date.now()): PriceStatus => {
  if (data?.price) {
    const outdated = data.updatedAt !== undefined && now - data.updatedAt > PRICE_STALE_AFTER_MS
    return error || outdated ? 'stale' : 'ready'
  }
  return error ? 'unavailable' : 'loading'
}
